import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import EventCard from '../../components/EventCard/EventCard';
import { LoadingSpinner } from '../../components';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './EventsPage.css';
import { list_events, count_events, get_user_info } from '../../api/functions';
import { useApi } from '../../api/api';
import { useAuth } from '../../provider/auth';

const Events = () => {
  const { t, i18n } = useTranslation();
  const listUpcomingEventsApi = useApi(list_events, 350);
  const listPastEventsApi = useApi(list_events, 350);
  const listAllEventsApi = useApi(list_events, 350);
  const countPastEventsApi = useApi(count_events);
  const getUserInfoApi = useApi(get_user_info);
  const { token } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [selectedDate, setSelectedDate] = useState(null);
  const [pastEventsPage, setPastEventsPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [pastEventsLoading, setPastEventsLoading] = useState(false);
  const [batchSize] = useState(36);
  const [totalPastEvents, setTotalPastEvents] = useState(0);
  useEffect(() => {
    const fetchUpcomingAndCounts = async () => {
      setEventsLoading(true);
      const userInfo = await getUserInfoApi.execute();
      const showPrivate = userInfo?.is_member || false;
      const language = (i18n.language === 'cs' ? 'cz' : i18n.language) || 'en';
      if (viewMode === 'calendar') {
        await listAllEventsApi.execute({
          skip: 0,
          limit: 500,
          show_private: showPrivate,
          search_query: searchQuery || undefined,
          language,
          time_filter: "all"
        });
        setEventsLoading(false);
      } else {
        const upcomingPromise = listUpcomingEventsApi.execute({
          skip: 0,
          limit: 500,
          show_private: showPrivate,
          search_query: searchQuery || undefined,
          language,
          time_filter: "upcoming"
        });

        const countPromise = countPastEventsApi.execute({
          show_private: showPrivate,
          search_query: searchQuery || undefined,
          language,
          time_filter: "past"
        });

        const [upcomingResult, countResult] = await Promise.all([
          upcomingPromise,
          countPromise
        ]);

        if (countResult && countResult.count !== undefined) {
          setTotalPastEvents(countResult.count);
        }

        setEventsLoading(false);
      }
    };
    fetchUpcomingAndCounts();
  }, [token, viewMode, searchQuery, i18n.language]);

  useEffect(() => {
    const fetchPastEvents = async () => {
      if (viewMode !== 'list') return;

      setPastEventsLoading(true);
      const userInfo = await getUserInfoApi.execute();
      const showPrivate = userInfo?.is_member || false;
      const language = i18n.language || 'en';

      const firstItemIndex = (pastEventsPage - 1) * itemsPerPage;
      const batchIndex = Math.floor(firstItemIndex / batchSize);
      const skip = batchIndex * batchSize;

      await listPastEventsApi.execute({
        skip,
        limit: batchSize,
        show_private: showPrivate,
        search_query: searchQuery || undefined,
        language,
        time_filter: "past"
      });
      setPastEventsLoading(false);
    };
    fetchPastEvents();
  }, [pastEventsPage, itemsPerPage, batchSize, searchQuery, i18n.language, viewMode]);

  const allEvents = viewMode === 'calendar' ? (listAllEventsApi.data || []) : [];
  const upcomingEvents = viewMode === 'list' ? (listUpcomingEventsApi.data || []) : [];
  const pastEventsData = viewMode === 'list' ? (listPastEventsApi.data || []) : [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filterEvents = (events) => {
    if (viewMode === 'calendar' && searchQuery) {
      return events.filter(event =>
        event.title_en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.text_en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.location_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.location_address?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return events;
  };

  const calendarUpcomingEvents = viewMode === 'calendar'
    ? allEvents.filter(event => new Date(`${event.event_start_date}T${event.event_start_time}`) >= today)
      .sort((a, b) => new Date(`${a.event_start_date}T${a.event_start_time}`) - new Date(`${b.event_start_date}T${b.event_start_time}`))
    : [];

  const calendarPastEvents = viewMode === 'calendar'
    ? allEvents.filter(event => new Date(`${event.event_start_date}T${event.event_start_time}`) < today)
      .sort((a, b) => new Date(`${b.event_start_date}T${b.event_start_time}`) - new Date(`${a.event_start_date}T${a.event_start_time}`))
    : [];

  const filteredUpcomingEvents = viewMode === 'calendar'
    ? filterEvents(calendarUpcomingEvents)
    : upcomingEvents;

  const filteredPastEvents = viewMode === 'calendar'
    ? filterEvents(calendarPastEvents)
    : pastEventsData;

  const groupEventsByMonth = (events) => {
    const langs = {
      'en': 'en-GB',
      'de': 'de-DE',
      'cz': 'cs-CZ'
    };
    const locale = langs[i18n.language] || 'cs-CZ';

    const grouped = {};
    events.forEach(event => {
      const date = new Date(`${event.event_start_date}T${event.event_start_time}`);
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

      if (!grouped[monthYear]) {
        grouped[monthYear] = {
          monthName,
          events: [],
          date: date
        };
      }
      grouped[monthYear].events.push(event);
    });

    return Object.values(grouped).sort((a, b) => a.date - b.date);
  };

  const groupedUpcomingEvents = groupEventsByMonth(filteredUpcomingEvents);

  let displayPastEvents, totalPastPages;
  if (viewMode === 'calendar') {
    totalPastPages = Math.ceil(filteredPastEvents.length / itemsPerPage);
    const startIndex = (pastEventsPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    displayPastEvents = filteredPastEvents.slice(startIndex, endIndex);
  } else {
    const firstItemIndex = (pastEventsPage - 1) * itemsPerPage;
    const batchIndex = Math.floor(firstItemIndex / batchSize);
    const batchStartIndex = batchIndex * batchSize;
    const offsetInBatch = firstItemIndex - batchStartIndex;

    displayPastEvents = filteredPastEvents.slice(offsetInBatch, offsetInBatch + itemsPerPage);
    totalPastPages = Math.ceil(totalPastEvents / itemsPerPage);
  }

  useEffect(() => {
    const updateItemsPerPage = () => {
      const width = window.innerWidth;
      if (width < 960) {
        setItemsPerPage(4);
      } else if (width < 1440) {
        setItemsPerPage(6);
      } else {
        setItemsPerPage(6);
      }
      setPastEventsPage(1);
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  const getEventsForDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return allEvents.filter(event => {
      const startDate = event.event_start_date;
      const endDate = event.event_end_date;
      return dateStr >= startDate && dateStr <= endDate;
    });
  };

  const hasEventsOnDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return allEvents.some(event => {
      const startDate = event.event_start_date;
      const endDate = event.event_end_date;
      return dateStr >= startDate && dateStr <= endDate;
    });
  };

  const tileContent = ({ date, view }) => {
    if (view === 'month' && hasEventsOnDate(date)) {
      return <div className="calendar-event-dot"></div>;
    }
    return null;
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPastEventsPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPastPages) {
      setPastEventsPage(page);
    }
  };

  const getPastPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 7;

    if (totalPastPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPastPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      let startPage = Math.max(2, pastEventsPage - 2);
      let endPage = Math.min(totalPastPages - 1, pastEventsPage + 2);

      if (startPage > 2) {
        pageNumbers.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPastPages - 1) {
        pageNumbers.push('...');
      }

      pageNumbers.push(totalPastPages);
    }

    return pageNumbers;
  };

  useEffect(() => {
    if (totalPastPages > 0 && pastEventsPage > totalPastPages) {
      setPastEventsPage(totalPastPages);
    }
  }, [totalPastPages, pastEventsPage]);

  return (
    <div className="events-page">
      <main className="events-page-main">
        <div className="events-page-container">
          <div className="events-page-header">
            <h1 className="events-page-title">{t('events.title')}</h1>

            <div className="events-page-controls">
              <div className="events-page-search">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="8" strokeWidth="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder={t('events.searchPlaceholder') || 'Search events...'}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="events-page-search-input"
                />
                {searchQuery && (
                  <button
                    className="events-page-search-clear"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              <div className="events-view-toggle">
                <button
                  className={`events-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  aria-label="List view"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <line x1="8" y1="6" x2="21" y2="6" strokeWidth="2" strokeLinecap="round" />
                    <line x1="8" y1="12" x2="21" y2="12" strokeWidth="2" strokeLinecap="round" />
                    <line x1="8" y1="18" x2="21" y2="18" strokeWidth="2" strokeLinecap="round" />
                    <line x1="3" y1="6" x2="3.01" y2="6" strokeWidth="2" strokeLinecap="round" />
                    <line x1="3" y1="12" x2="3.01" y2="12" strokeWidth="2" strokeLinecap="round" />
                    <line x1="3" y1="18" x2="3.01" y2="18" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  { }
                </button>
                <button
                  className={`events-view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                  onClick={() => setViewMode('calendar')}
                  aria-label="Calendar view"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
                    <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" strokeLinecap="round" />
                    <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" strokeLinecap="round" />
                    <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" />
                  </svg>
                  { }
                </button>
              </div>
            </div>
          </div>

          {eventsLoading ? (
            <LoadingSpinner message={t('events.loading') || 'Loading events...'} />
          ) : viewMode === 'calendar' ? (
            <div className="events-calendar-view">
              <div className="events-calendar-container">
                <Calendar
                  onChange={handleDateChange}
                  value={selectedDate}
                  tileContent={tileContent}
                  tileClassName={({ date }) => hasEventsOnDate(date) ? 'has-events' : null}
                  locale={t('locale')}
                />
              </div>

              {selectedDate && (
                <div className="events-calendar-selected">
                  <h2 className="events-calendar-selected-title">
                    {selectedDate.toLocaleDateString(
                      i18n.language === 'en' ? 'en-US' : i18n.language === 'de' ? 'de-DE' : 'cs-CZ',
                      { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
                    )}
                  </h2>
                  {selectedDateEvents.length > 0 ? (
                    <div className="events-calendar-selected-grid">
                      {selectedDateEvents.map((event, index) => (
                        <div key={event.id} className="fade-in-stagger">
                          <EventCard event={event} index={index} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="events-calendar-no-events">
                      <p>{t('events.noEventsOnDate') || 'No events on this date'}</p>
                    </div>
                  )}
                </div>
              )}

              {!selectedDate && (
                <div className="events-calendar-prompt">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
                    <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" strokeLinecap="round" />
                    <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" strokeLinecap="round" />
                    <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" />
                  </svg>
                  <p>{t('events.selectDate') || 'Select a date to view events'}</p>
                </div>
              )}
            </div>
          ) : (
            <>
              {groupedUpcomingEvents.length > 0 && (
                <section className="events-upcoming-section">
                  <h2 className="events-section-title">{t('events.upcomingEvents') || 'Upcoming Events'}</h2>

                  {groupedUpcomingEvents.map((monthGroup) => (
                    <div key={monthGroup.monthName} className="events-month-group">
                      <h3 className="events-month-title">{monthGroup.monthName}</h3>
                      <div className="events-page-grid">
                        {monthGroup.events.map((event, index) => (
                          <div key={event.id} className="fade-in-stagger">
                            <EventCard event={event} index={index} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </section>
              )}

              {filteredPastEvents.length > 0 && (
                <section className="events-past-section">
                  <h2 className="events-section-title">{t('events.pastEvents') || 'Past Events'}</h2>

                  {pastEventsLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0', minHeight: '400px' }}>
                      <LoadingSpinner size="medium" />
                    </div>
                  ) : (
                    <div className="events-page-grid">
                      {displayPastEvents.map((event, index) => (
                        <div key={event.id} className="fade-in-stagger">
                          <EventCard event={event} index={index} />
                        </div>
                      ))}
                    </div>
                  )}

                  {totalPastPages > 1 && (
                    <div className="events-pagination">
                      <button
                        className="events-pagination-btn events-pagination-prev"
                        onClick={() => handlePageChange(pastEventsPage - 1)}
                        disabled={pastEventsPage === 1}
                        aria-label="Previous page"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      <div className="events-pagination-numbers">
                        {getPastPageNumbers().map((pageNum, index) => (
                          pageNum === '...' ? (
                            <span key={`ellipsis-${index}`} className="events-pagination-ellipsis">
                              ...
                            </span>
                          ) : (
                            <button
                              key={pageNum}
                              className={`events-pagination-number ${pageNum === pastEventsPage ? 'active' : ''}`}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </button>
                          )
                        ))}
                      </div>

                      <button
                        className="events-pagination-btn events-pagination-next"
                        onClick={() => handlePageChange(pastEventsPage + 1)}
                        disabled={pastEventsPage === totalPastPages}
                        aria-label="Next page"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </section>
              )}

              {filteredUpcomingEvents.length === 0 && filteredPastEvents.length === 0 && (
                <div className="events-empty">
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
                    <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" strokeLinecap="round" />
                    <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" strokeLinecap="round" />
                    <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" />
                  </svg>
                  <p>{t('events.noEventsFound')}</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Events;
